import { useUpdateApPayeeInfo } from '@/context/services/ap_payee/UpdateApPayeeInfo.service';
import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { SECTION_KEYS } from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useState } from 'react';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { transformPayeeFormData } from '../helpers/transformPayeeFormData';

type UseSubmitPayeeInfoParams = {
  apPayeeId: string;
};

export const useSubmitPayeeInfo = <TFormData extends FieldValues>({
  apPayeeId,
}: UseSubmitPayeeInfoParams) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const { refetch, setIsEditingSection, onClose } = usePayeeDetailDrawer();
  const [isLoading, setIsLoading] = useState(false);

  const { mutateAsync } = useUpdateApPayeeInfo();

  const onSubmit: SubmitHandler<TFormData> = useCallback(
    async (formData) => {
      setIsLoading(true);
      try {
        const transformedData = transformPayeeFormData(formData);

        const resp = await mutateAsync({
          id: apPayeeId,
          data: {
            ap_payee: transformedData,
          },
        });

        if (resp.status === 'success') {
          await refetch();
          setIsEditingSection(SECTION_KEYS.NULL);
          notify.success({
            title: t('ap_payees_detail_drawer.update.success'),
            duration: 5,
          });
        } else if (resp.status === 'error' && resp?.statusCode === 404) {
          /* Payee was deleted - close drawer first, then show toast, then reload payee list page
           */
          // Close drawer first
          onClose?.();

          // Show toast after drawer closes
          notify.error({
            title: t('ap_payees_detail_drawer.update.payee_deleted'),
            duration: 5,
          });

          // Delay to ensure toast is visible and then go to new path or reload
          // TODO: temp set to 2s to ensure the toast is visible and then go to new path or reload, we will have enhancement on next sprint
          setTimeout(() => {
            // Preserve search conditions but reset 'page' to 1
            const searchParams = new URLSearchParams(window.location.search);
            searchParams.delete('page'); // Remove page if present
            const newSearch = searchParams.toString();
            const newPath = `/ap_payees${newSearch ? `?${newSearch}` : ''}`;
            const currentPath = `${window.location.pathname}${window.location.search}`;
            // If URL doesn't change, force reload; otherwise go to new path
            if (currentPath === newPath) {
              window.location.reload();
            } else {
              window.location.href = newPath;
            }
          }, 2000);
        } else if (resp.status === 'error') {
          notify.error({
            title: t('ap_payees_detail_drawer.update.failed'),
            duration: 5,
          });
        } else {
          notify.error({
            title: t('system_error'),
            duration: 5,
          });
        }
      } catch (error) {
        notify.error({
          title: error instanceof Error ? error.message : t('system_error'),
          duration: 5,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [apPayeeId, mutateAsync, notify, onClose, refetch, setIsEditingSection, t]
  );

  return { onSubmit, isLoading };
};
